import { CloudArrowDownIcon } from '@heroicons/react/24/outline';
import { useCallback } from 'react';

import { Grid } from '@/components/core/Grid';
import { Panel, PanelHeader } from '@/components/core/Panel';
import { PanelBody } from '@/components/core/Panel/PanelBody';
import { useDynamicPageSize } from '@/hooks/useDynamicPageSize';
import useRealTimeMessaging from '@/hooks/useRealtimeMessaging';
import { useTranslation } from '@/hooks/useTranslation';
import { RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

import { columns } from './columns';
import { useExportData, useUpdateExportData } from './export.queries';
import { ExportDataType } from './export.types';

export default function ExportPage() {

    const { t } = useTranslation();

    const exportGridColumns = columns(t);

    const pageSize = useDynamicPageSize();

    const updateExportMutation = useUpdateExportData();

    const { data: exportingData, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage  } = useExportData({
        variables: {
            limit: pageSize
        },

        select: data => data.pages.flatMap((page) => page.data),

        enabled: pageSize > 0
    });

    const onExportSummaryChangedCallback = useCallback((exportEntryEventData: ExportDataType) => {
        if (exportEntryEventData.status !== undefined) {
            updateExportMutation.mutate({ exportEntry: exportEntryEventData, cacheQueryVariables: { limit: pageSize } });
        }
    }, [updateExportMutation, pageSize]);

    useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.EXPORT_SUMMARY_CHANGED_EVENT,
        eventHandler: onExportSummaryChangedCallback
    });

    const handleInfiniteScroll = useCallback(() => {
        if (hasNextPage) {
            fetchNextPage();
        }
    }, [hasNextPage, fetchNextPage]);

    return (
        <Panel>
            <PanelHeader icon={CloudArrowDownIcon} title={t('EXPORTS')} />
            <PanelBody>
                <Grid
                    columns={exportGridColumns}
                    isLoading={isLoading}
                    storedStateKey={'Export'}
                    isLoadingNextPage={isFetchingNextPage}
                    data={exportingData || []}
                    onInfiniteScroll={handleInfiniteScroll}
                    isWrapContent={true}
                    className='tw-h-full'
                />
            </PanelBody>
        </Panel>
    );
}
